import { useEffect } from 'react';

/**
 * Close an overlay when Escape is pressed.
 *
 * `Modal` already does this for centered dialogs. Drawers and slide-overs have
 * their own layout and are not worth forcing through the Modal shell, but they
 * must still honour Escape — a user who learns that Escape closes one overlay
 * expects it to close the next one, and before this hook only one of the app's
 * overlays did.
 *
 * @param enabled Whether the overlay is currently open.
 * @param onDismiss Called on Escape.
 */
export function useDismissOnEscape(enabled: boolean, onDismiss: () => void): void {
  useEffect(() => {
    if (!enabled) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Stop the page underneath from also acting on Escape — several screens
      // bind it to "go back", which would navigate away as the overlay closes.
      e.stopPropagation();
      onDismiss();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [enabled, onDismiss]);
}

export default useDismissOnEscape;
